import { NavItem } from '../constants/sidebar';

export type FlattenedNavItem = {
  code: string;
  name: string;
  urlPath: string;
  fullPath: string; // Hierarchical name, e.g., "Manutenção Preventiva > Condominio > Plano de Manutenção"
  depth: number;
};

/**
 * Recursively flattens the navigation tree into a flat array for indexing/searching
 * @param navItems - The navigation items to flatten (should already have complete URLs)
 * @param parentPath - The hierarchical path of parent names
 * @returns A flat array of navigation items with full path context
 */
export function flattenNavItems(
  navItems: NavItem[], 
  parentPath: string[] = []
): FlattenedNavItem[] {
  return navItems.flatMap(item => {
    // Skip items without required properties
    const code = item.code || item.id;
    const name = item.name || item.title;
    const urlPath = item.urlPath || item.path;
    
    // console.log(`[flattenNavItems] Processing item:`, {
    //   original: item,
    //   extracted: { code, name, urlPath }
    // });
    
    if (!code || !name) {
      // console.log(`[flattenNavItems] Skipping item due to missing code or name:`, { code, name, urlPath });
      return []; // Skip this item if essential data is missing
    }
    
    const currentPath = [...parentPath, name];
    const base: FlattenedNavItem = {
      code,
      name,
      urlPath: urlPath || '', // Allow empty urlPath for parent items
      fullPath: currentPath.join(' > '),
      depth: currentPath.length
    };
    
    // console.log(`[flattenNavItems] Created flattened item:`, base);
    
    // If item has children, include the parent and recursively flatten children
    return item.children ? 
      [base, ...flattenNavItems(item.children, currentPath)] : 
      [base];
  });
}

/**
 * Gets all leaf items (items without children) from the navigation tree
 * @param items - The navigation items (should already have complete URLs)
 * @returns Only leaf items that can be navigated to
 */
export function getLeafItems(items: NavItem[]): FlattenedNavItem[] {
  const result: FlattenedNavItem[] = [];
  
  const processItems = (navItems: NavItem[], parentPath: string[] = []) => {
    navItems.forEach(item => {
      const code = item.code || item.id;
      const name = item.name || item.title;
      const urlPath = item.urlPath || item.path;
      
      if (!code || !name || !urlPath) {
        return; // Skip this item if essential data is missing
      }
      
      const currentPath = [...parentPath, name];
      
      if (!item.children || item.children.length === 0) {
        result.push({
          code,
          name,
          urlPath, // This should already be the complete URL
          fullPath: currentPath.join(' > '),
          depth: currentPath.length
        });
      } else {
        processItems(item.children, currentPath);
      }
    });
  };
  
  processItems(items);
  return result;
}

/**
 * Gets searchable items from flattened navigation
 * @param items - The flattened navigation items
 * @returns All items that can be searched
 */
export function getSearchableItems(items: FlattenedNavItem[]): FlattenedNavItem[] {
  // All items are searchable in the new structure
  return items;
}